import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { TrendingUp, Calendar, ArrowUpRight } from 'lucide-react';
import { useIsDark } from '../../useIsDark';

// Month-by-month data: cumulative net value on $500/mo cost
const months = Array.from({ length: 24 }, (_, i) => `M${i + 1}`);

// Cumulative value curve: starts slow, compounds as bots earn autonomy
const cumulativeValue = [
  800, 2100, 3800, 5900, 8500, 11600, 15200, 19300, 23900, 29100,
  34900, 41400, 48700, 56800, 65800, 75700, 86600, 98600, 111700,
  126000, 141500, 158300, 176400, 195900,
];

const cumulativeCost = months.map((_, i) => (i + 1) * 500);
const netValue = cumulativeValue.map((v, i) => v - cumulativeCost[i]);

const milestoneMonths = [6, 12, 18, 24];
const milestoneLabels: Record<number, string> = {
  6: 'Department Ready',
  12: 'Full Autonomy',
  18: 'Scaling Mode',
  24: 'Market Dominance',
};

export const ROIGrowthChart = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isDark = useIsDark();

  // Canvas chrome follows the class-based theme; series colors stay identical.
  const C = isDark
    ? {
        tooltipBg: 'rgba(15, 23, 42, 0.95)',
        tooltipBorder: '#334155',
        tooltipText: '#f8fafc',
        value: '#4DD4E0',
        cost: '#f87171',
        net: '#34d399',
        muted: '#94a3b8',
        grid: '#1e293b',
      }
    : {
        tooltipBg: '#ffffff',
        tooltipBorder: '#e2e8f0',
        tooltipText: '#0f172a',
        value: '#087B8B',
        cost: '#f87171',
        net: '#059669',
        muted: '#64748b',
        grid: '#e2e8f0',
      };

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: C.tooltipBg,
      borderColor: C.tooltipBorder,
      textStyle: { color: C.tooltipText },
      formatter: (params: any[]) => {
        const month = params[0].axisValue;
        const net = params.find((p) => p.seriesName === 'Net Value')?.value ?? 0;
        const cost = params.find((p) => p.seriesName === 'Cumulative Cost')?.value ?? 0;
        const gross = params.find((p) => p.seriesName === 'Value Created')?.value ?? 0;
        const roi = cost > 0 ? Math.round((net / cost) * 100) : 0;
        return `
          <div style="font-weight:600;margin-bottom:4px">${month}</div>
          <div style="color:${C.value}">Value Created: $${gross.toLocaleString()}</div>
          <div style="color:${C.cost}">Cumulative Cost: $${cost.toLocaleString()}</div>
          <div style="color:${C.net};font-weight:600;margin-top:4px">Net Value: $${net.toLocaleString()}</div>
          <div style="color:${C.muted};font-size:0.6875rem">ROI: ${roi}%</div>
        `;
      },
    },
    legend: {
      data: ['Value Created', 'Cumulative Cost', 'Net Value'],
      textStyle: { color: C.muted },
      bottom: 0,
    },
    grid: {
      left: 60,
      right: 30,
      top: 40,
      bottom: 50,
    },
    xAxis: {
      type: 'category',
      data: months,
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        interval: 2,
        rotate: 30,
      },
      axisLine: { lineStyle: { color: '#334155' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        formatter: (val: number) => `$${(val / 1000)}K`,
      },
      splitLine: { lineStyle: { color: C.grid } },
    },
    series: [
      {
        name: 'Value Created',
        type: 'line',
        data: cumulativeValue,
        smooth: 0.4,
        symbol: 'none',
        lineStyle: {
          width: 3,
          color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: '#0AB3BF' },
            { offset: 1, color: '#4DD4E0' },
          ]),
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(10, 179, 191, 0.25)' },
            { offset: 1, color: 'rgba(10, 179, 191, 0.02)' },
          ]),
        },
        animationDuration: 2000,
        animationEasing: 'cubicOut',
      },
      {
        name: 'Cumulative Cost',
        type: 'line',
        data: cumulativeCost,
        smooth: 0,
        symbol: 'none',
        lineStyle: {
          width: 2,
          type: 'dashed',
          color: '#f87171',
        },
        animationDuration: 2000,
        animationDelay: 500,
      },
      {
        name: 'Net Value',
        type: 'line',
        data: netValue,
        smooth: 0.4,
        symbol: 'none',
        lineStyle: {
          width: 3,
          color: '#34d399',
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(52, 211, 153, 0.15)' },
            { offset: 1, color: 'rgba(52, 211, 153, 0.02)' },
          ]),
        },
        animationDuration: 2000,
        animationDelay: 800,
        animationEasing: 'cubicOut',
      },
      // Milestone markers
      {
        type: 'scatter',
        data: milestoneMonths.map((m) => [months[m - 1], cumulativeValue[m - 1]]),
        symbolSize: 12,
        itemStyle: {
          color: '#fbbf24',
          borderColor: '#fff',
          borderWidth: 2,
          shadowBlur: 10,
          shadowColor: '#fbbf24',
        },
        label: {
          show: true,
          position: 'top',
          formatter: (p: any) => milestoneLabels[parseInt(p.name.replace('M', ''))] || '',
          color: '#fbbf24',
          fontSize: 10,
          fontWeight: 600,
        },
        animationDuration: 1000,
        animationDelay: 2000,
      },
    ],
  };

  const month12Net = netValue[11];
  const month12Cost = cumulativeCost[11];
  const month12ROI = Math.round((month12Net / month12Cost) * 100);

  return (
    <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 rounded-full text-sm font-medium border dark:border-teal-500/20 mb-6">
          <Calendar className="w-4 h-4" />
          Compounding Returns Over Time
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          Value That{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500 dark:from-teal-400 dark:to-emerald-400">
            Compounds
          </span>
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          Every approval teaches every bot. A correction at the source ripples across every bot in your workforce.
          The more you use Raisy, the more valuable it becomes.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Chart */}
        <motion.div
          className="lg:col-span-2 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700/50 rounded-xl p-6 backdrop-blur-sm"
          initial={{ opacity: 0, x: -30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">24-Month Value Trajectory</h3>
            </div>
            <span className="text-xs text-slate-500">Based on $500/mo cost</span>
          </div>
          <ReactECharts
            option={option}
            style={{ height: '380px', width: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        </motion.div>

        {/* Key Stats */}
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, x: 30 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {/* Month 12 highlight */}
          <div className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-200 dark:border-teal-500/30 rounded-xl p-6 backdrop-blur-sm">
            <div className="text-sm text-teal-600 dark:text-teal-400 font-medium mb-2">At Month 12</div>
            <div className="text-4xl font-bold text-slate-900 dark:text-white">${(month12Net / 1000).toFixed(0)}K</div>
            <div className="text-lg font-semibold text-teal-600 dark:text-teal-400">net value generated</div>
            <div className="flex items-center gap-1 mt-2">
              <ArrowUpRight className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">{month12ROI}% ROI</span>
              <span className="text-sm text-slate-500">on ${(month12Cost / 1000).toFixed(0)}K invested</span>
            </div>
          </div>

          {/* Month 24 */}
          <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/40 rounded-xl p-6 backdrop-blur-sm">
            <div className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-2">At Month 24</div>
            <div className="text-4xl font-bold text-slate-900 dark:text-white">${(netValue[23] / 1000).toFixed(0)}K</div>
            <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">net value generated</div>
            <div className="text-sm text-slate-500 mt-2">
              ${(cumulativeValue[23] / 1000).toFixed(0)}K value created on ${(cumulativeCost[23] / 1000).toFixed(0)}K cost
            </div>
          </div>

          {/* Key insight */}
          <div className="bg-white dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/30 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Why it compounds</h4>
            <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 dark:bg-teal-400 mt-1.5 flex-shrink-0" />
                One correction improves all bots using that capability
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 dark:bg-teal-400 mt-1.5 flex-shrink-0" />
                Each bot's confidence score evolves independently
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 dark:bg-teal-400 mt-1.5 flex-shrink-0" />
                New bots inherit knowledge from existing ones
              </div>
              <div className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 dark:bg-teal-400 mt-1.5 flex-shrink-0" />
                Human review time drops as autonomy grows
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
